import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Button,
  Fade,
  useTheme,
  useMediaQuery,
  Divider,
  Avatar
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import RocketIcon from '@mui/icons-material/Rocket';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PushPinIcon from '@mui/icons-material/PushPin';
import StarIcon from '@mui/icons-material/Star';
import logo0 from '../assets/logo0.png'; // Make sure this path is correct

// Animations
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 200, 83, 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(0, 200, 83, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 200, 83, 0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

// Styled components for enhanced visual appeal
const AccountCard = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(4),
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.97)',
  borderRadius: 0,
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  overflow: 'hidden',
  transform: selected ? 'scale(1.03)' : 'scale(1)',
  boxShadow: selected 
    ? '0 20px 30px rgba(0, 0, 0, 0.25)' 
    : '0 5px 15px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 20px 30px rgba(0, 0, 0, 0.25)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '5px',
    background: 'linear-gradient(90deg, #00C853 0%, #69F0AE 100%)',
  },
  '&::after': {
    content: selected ? '""' : 'none',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '5px',
    background: 'linear-gradient(90deg, #00C853 0%, #69F0AE 100%)',
  }
}));

const FeatureItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1.2, 0),
  borderRadius: '4px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 200, 83, 0.08)',
    transform: 'translateX(5px)',
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(3),
  background: 'linear-gradient(90deg, #00C853 0%, #69F0AE 100%)',
  color: 'white',
  padding: '14px 28px',
  fontWeight: 700,
  fontSize: '1rem',
  letterSpacing: '0.5px',
  transition: 'all 0.3s ease',
  borderRadius: '4px',
  textTransform: 'none',
  '&:hover': {
    background: 'linear-gradient(90deg, #00B34A 0%, #56E39F 100%)',
    boxShadow: '0 5px 15px rgba(0, 200, 83, 0.4)',
    transform: 'translateY(-3px)',
  },
  '&:active': {
    transform: 'translateY(1px)',
  }
}));

const IconBox = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
  background: 'linear-gradient(135deg, #00C853 0%, #69F0AE 100%)',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 5px 15px rgba(0, 200, 83, 0.3)',
  animation: `${pulse} 2s infinite`,
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 30,
  left: 30,
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  boxShadow: '0 5px 20px rgba(0, 0, 0, 0.2)',
  animation: `${float} 6s ease-in-out infinite`,
}));

const FeatureTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  color: '#00C853',
  textTransform: 'uppercase',
  position: 'relative',
  display: 'inline-block',
  marginBottom: theme.spacing(3),
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: '80px',
    height: '4px',
    background: 'linear-gradient(90deg, #00C853 0%, #69F0AE 100%)',
  }
}));

const HighlightText = styled('span')(({ theme }) => ({
  color: '#00C853',
  fontWeight: 700,
}));

const Numbers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    // Auto-select the first account after a delay for visual effect
    const timer = setTimeout(() => {
      setSelectedAccount('individual');
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ 
      minHeight: '100vh',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      py: { xs: 8, md: 12 },
      overflow: 'hidden',
      backgroundColor: '#0a192f', // Deep blue background for better contrast
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `linear-gradient(135deg, rgba(0, 200, 83, 0.1) 0%, rgba(0, 200, 83, 0.05) 100%)`,
        backgroundSize: 'cover',
        zIndex: 0,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: 'radial-gradient(circle, rgba(105, 240, 174, 0.1) 1px, transparent 1px)',
        backgroundSize: '20px 20px',
        zIndex: 0,
      }
    }}>
      {/* Decorative elements */}
      <Box sx={{
        position: 'absolute',
        top: '-100px',
        right: '-100px',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(0, 200, 83, 0.2) 0%, rgba(105, 240, 174, 0.2) 100%)',
        zIndex: 0,
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: '-100px',
        left: '-100px',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(0, 200, 83, 0.2) 0%, rgba(105, 240, 174, 0.2) 100%)',
        zIndex: 0,
      }} />
      
      {/* Enhanced Logo */}
      <Fade in={isLoaded} timeout={1000}>
        <LogoContainer>
          <Box 
            component="img" 
            src={logo0} 
            alt="Logo" 
            sx={{ 
              height: '60px',
              filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.2))'
            }} 
          />
        </LogoContainer>
      </Fade>

      <Container maxWidth="lg">
        {/* Header */}
        <Fade in={isLoaded} timeout={1000}>
          <Box sx={{ 
            textAlign: 'center', 
            mb: { xs: 5, md: 8 },
            position: 'relative',
            zIndex: 1
          }}>
            <Typography 
              variant="h2" 
              component="h1" 
              sx={{ 
                fontWeight: 900, 
                mb: 2,
                color: '#fff',
                textTransform: 'uppercase',
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                fontSize: { xs: '2.2rem', sm: '2.7rem', md: '3.2rem' },
                letterSpacing: '1px'
              }}
            >
              CHOOSE YOUR ACCOUNT TYPE
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                mb: 5, 
                color: '#fff',
                textTransform: 'uppercase',
                textShadow: '0 2px 5px rgba(0,0,0,0.3)',
                fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                maxWidth: '800px',
                mx: 'auto',
                letterSpacing: '0.5px'
              }}
            >
              SELECT THE ACCOUNT TYPE THAT BEST SUITS YOUR NEEDS
            </Typography>
          </Box>
        </Fade>
        
        {/* Account Types */}
        <Grid container spacing={5}>
          {/* Individual Account */}
          <Grid item xs={12} md={6}>
            <Fade in={isLoaded} timeout={1200} style={{ transitionDelay: '200ms' }}>
              <Box>
                <AccountCard 
                  elevation={selectedAccount === 'individual' ? 10 : 3} 
                  selected={selectedAccount === 'individual'}
                  onClick={() => setSelectedAccount('individual')}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconBox>
                      <PersonIcon sx={{ color: '#fff', fontSize: 40 }} />
                    </IconBox>
                    <Typography variant="h4" component="h2" sx={{ 
                      fontWeight: 800, 
                      textTransform: 'uppercase',
                      fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2rem' },
                      letterSpacing: '0.5px',
                      background: 'linear-gradient(90deg, #000 30%, #333 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      INDIVIDUAL ACCOUNT
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3,
                    p: 2,
                    borderRadius: '4px',
                    backgroundColor: 'rgba(0, 200, 83, 0.08)',
                  }}>
                    <GpsFixedIcon sx={{ color: '#00C853', mr: 1, fontSize: '1.3rem' }} />
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 600,
                      color: 'rgba(0,0,0,0.8)',
                      fontSize: '1.05rem',
                    }}>
                      <HighlightText>Ideal For:</HighlightText> Home users, students, and tech enthusiasts who need AI-powered fault detection and repair assistance.
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 4 }}>
                    <FeatureTitle variant="h6">
                      FEATURES
                    </FeatureTitle>
                  </Box>
                  
                  <List>
                    {[
                      "AI-Powered Fault Diagnosis – Upload images or describe issues to get instant troubleshooting solutions.",
                      "Cloud-Based Repair History – Track past repairs and diagnostics.",
                      "DIY Video Guides – Step-by-step tutorials for fixing common problems.",
                      "Access to Genuine Spare Parts – Purchase verified replacement components.",
                      "Virtual Expert Consultations – Book video calls with certified repair technicians.",
                      "Smart AI Chatbot Support – Get real-time assistance 24/7.",
                      "Free & Premium Service Tiers – Free troubleshooting with optional paid repair services.",
                      "Community Forum Access – Engage with other users and share repair experiences."
                    ].map((feature, index) => (
                      <FeatureItem key={index}>
                        <ListItemIcon sx={{ minWidth: 35 }}>
                          <CheckCircleIcon sx={{ color: '#00C853', fontSize: '1.3rem' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ 
                            fontWeight: 500,
                            fontSize: '1rem',
                            lineHeight: 1.4,
                            color: 'rgba(0,0,0,0.8)'
                          }} 
                        />
                      </FeatureItem>
                    ))}
                  </List>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <StarIcon key={star} sx={{ color: '#FFC107', fontSize: '1.2rem' }} />
                      ))}
                      <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>
                        Recommended for individuals
                      </Typography>
                    </Box>
                    <ActionButton 
                      variant="contained" 
                      endIcon={<ArrowForwardIcon />}
                    >
                      Select Individual
                    </ActionButton>
                  </Box>
                </AccountCard>
              </Box>
            </Fade>
          </Grid>
          
          {/* Business Account */}
          <Grid item xs={12} md={6}>
            <Fade in={isLoaded} timeout={1200} style={{ transitionDelay: '400ms' }}>
              <Box>
                <AccountCard 
                  elevation={selectedAccount === 'business' ? 10 : 3} 
                  selected={selectedAccount === 'business'}
                  onClick={() => setSelectedAccount('business')}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconBox>
                      <BusinessIcon sx={{ color: '#fff', fontSize: 40 }} />
                    </IconBox>
                    <Typography variant="h4" component="h2" sx={{ 
                      fontWeight: 800, 
                      textTransform: 'uppercase',
                      fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2rem' },
                      letterSpacing: '0.5px',
                      background: 'linear-gradient(90deg, #000 30%, #333 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      BUSINESS ACCOUNT
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3,
                    p: 2,
                    borderRadius: '4px',
                    backgroundColor: 'rgba(0, 200, 83, 0.08)',
                  }}>
                    <GpsFixedIcon sx={{ color: '#00C853', mr: 1, fontSize: '1.3rem' }} />
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 600,
                      color: 'rgba(0,0,0,0.8)',
                      fontSize: '1.05rem',
                    }}>
                      <HighlightText>Ideal For:</HighlightText> Repair businesses, service providers, and tech retailers offering repair services and selling spare parts.
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <FeatureTitle variant="h6">
                      FEATURES
                    </FeatureTitle>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 2,
                    p: 1.5,
                    borderRadius: '4px',
                    backgroundColor: 'rgba(0, 200, 83, 0.05)',
                    border: '1px dashed rgba(0, 200, 83, 0.3)',
                  }}>
                    <RocketIcon sx={{ color: '#00C853', mr: 1, fontSize: '1.3rem' }} />
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#00C853'
                    }}>
                      All Features of Individual Accounts plus:
                    </Typography>
                  </Box>
                  
                  <List>
                    {[
                      "Bulk Device Diagnostics – Analyze multiple devices at once for faster troubleshooting.",
                      "Business Dashboard – Manage service requests, repair logs, and customer interactions.",
                      "Advanced AI Repair Insights – Get detailed reports and predictions for recurring faults.",
                      "Branded Storefront – List and sell genuine spare parts & accessories in the Warrili Marketplace.",
                      "Priority Expert Support – Faster response times and dedicated customer service.",
                      "Service Integration – Connect with logistics and shipping partners for part deliveries.",
                      "Fraud Protection & Secure Payments – Enhanced security for transactions and customer interactions.",
                      "API Access for Business Tools – Integrate with CRM, invoicing, and tracking systems.",
                      "Premium Business Listing – Get featured as a verified repair service provider."
                    ].map((feature, index) => (
                      <FeatureItem key={index}>
                        <ListItemIcon sx={{ minWidth: 35 }}>
                          <CheckCircleIcon sx={{ color: '#00C853', fontSize: '1.3rem' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ 
                            fontWeight: 500,
                            fontSize: '1rem',
                            lineHeight: 1.4,
                            color: 'rgba(0,0,0,0.8)'
                          }} 
                        />
                      </FeatureItem>
                    ))}
                  </List>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <StarIcon key={star} sx={{ color: '#FFC107', fontSize: '1.2rem' }} />
                      ))}
                      <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>
                        Recommended for businesses
                      </Typography>
                    </Box>
                    <ActionButton 
                      variant="contained" 
                      endIcon={<ArrowForwardIcon />}
                    >
                      Select Business
                    </ActionButton>
                  </Box>
                </AccountCard>
              </Box>
            </Fade>
          </Grid>
        </Grid>
        
        {/* Which Plan is Best for You? */}
        <Fade in={isLoaded} timeout={1200} style={{ transitionDelay: '600ms' }}>
          <Box sx={{ 
            mt: 6,
            p: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.97)',
            position: 'relative',
            zIndex: 1,
            boxShadow: '0 5px 20px rgba(0, 0, 0, 0.15)',
            borderLeft: '5px solid #00C853',
          }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 800, 
              mb: 3,
              color: '#00C853',
              textAlign: 'center',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Which Plan is Best for You?
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  mb: 2,
                  p: 2,
                  borderRadius: '4px',
                  backgroundColor: 'rgba(0, 200, 83, 0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 200, 83, 0.1)',
                    transform: 'translateY(-3px)',
                  }
                }}>
                  <PushPinIcon sx={{ color: '#00C853', mr: 1.5, mt: 0.5, fontSize: '1.3rem' }} />
                  <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.5 }}>
                    <strong style={{ color: '#00C853' }}>Choose Individual</strong> if you need personal repair assistance and access to AI-powered diagnostics.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  mb: 2,
                  p: 2,
                  borderRadius: '4px',
                  backgroundColor: 'rgba(0, 200, 83, 0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 200, 83, 0.1)',
                    transform: 'translateY(-3px)',
                  }
                }}>
                  <PushPinIcon sx={{ color: '#00C853', mr: 1.5, mt: 0.5, fontSize: '1.3rem' }} />
                  <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.5 }}>
                    <strong style={{ color: '#00C853' }}>Choose Business</strong> if you run a repair shop or sell spare parts and need advanced business tools.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 2,
              borderRadius: '4px',
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              border: '1px dashed rgba(255, 193, 7, 0.5)',
            }}>
              <LightbulbIcon sx={{ color: '#FFC107', mr: 2, fontSize: '2rem' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                Want custom enterprise solutions? We can tailor a plan to fit your needs! 🚀
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default Numbers; 